import { useQuery } from '@apollo/client';
import { faFileExport, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type {
  GeologyType,
  UploadStudyListPartsFragment,
} from '~/apollo/generated/v4/graphql';
import { Panel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { StudyList } from '~/components/upload/study/study-list';
import { useSortFilter } from '~/hooks/data';
import { exportStudiesRoute, uploadStudyCreateRoute } from '~/paths';
import { studyGeologyTypes } from '~/utils/modules/study';

export type SortableStudy = UploadStudyListPartsFragment & {
  hasOutcrops: boolean;
  searchableText: string;
  geologyTypes: GeologyType[];
};

export const toSortableStudy = (
  study: UploadStudyListPartsFragment,
): SortableStudy => ({
  ...study,
  outcrops: study.outcrops.slice().sort(R.ascend(oc => oc.name)),
  hasOutcrops: (study.outcrops ?? []).length > 0,
  searchableText: `${study.name} - ${study.collectedBy}`.toLowerCase(),
  geologyTypes: studyGeologyTypes(study),
});

export const uploadStudyListParts = graphql(`
  fragment uploadStudyListParts on Study {
    id
    name
    type
    collectedBy
    collectedYear
    publicationType
    lifecycleStatus
    qcCompleted
    redundant
    project {
      id
      name
    }
    outcrops {
      id
      name
    }
    architecturalElementCount
    measurementCount
    dominantGeologyType
    keyParameters {
      id
      geologyType
    }
  }
`);

const UPLOAD_STUDY_LIST = graphql(`
  query UploadStudyList {
    studyList {
      results {
        ...uploadStudyListParts
      }
    }
  }
`);

export default function UploadStudyListPage() {
  return (
    <ApolloProviderV4>
      <UploadStudyListPageInner />
    </ApolloProviderV4>
  );
}

function UploadStudyListPageInner() {
  const { data, loading } = useQuery(UPLOAD_STUDY_LIST);

  const sortableStudies = data?.studyList?.results?.map(toSortableStudy) ?? [];

  const { items, sortIndicatorProps, filterSearchProps } = useSortFilter(
    sortableStudies,
    'id',
    'searchableText',
    'uploadStudyList',
  );

  return (
    <Panel>
      <Panel.Heading className="flex justify-between items-center">
        <Panel.Title>
          Studies
          {!loading && (
            <small className="text-muted ml-2">
              (<strong>{items.length}</strong> results displayed)
            </small>
          )}
        </Panel.Title>

        <div className="space-x-2">
          <Link
            to={exportStudiesRoute()}
            target="_blank"
            className="btn btn-ghost btn-xs gap-1"
          >
            <FontAwesomeIcon icon={faFileExport} />
            Export Studies
          </Link>

          <Link
            to={uploadStudyCreateRoute()}
            className="btn btn-primary btn-xs gap-1"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Study
          </Link>
        </div>
      </Panel.Heading>
      <Panel.Body>
        <SpinnerPlaceholder show={loading} />
        {!loading && (
          <StudyList
            studies={items}
            sortIndicatorProps={sortIndicatorProps}
            filterSearchProps={filterSearchProps}
          />
        )}
      </Panel.Body>
    </Panel>
  );
}
